'use client';

import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';

import { useCallbackRef } from '../../../hooks/use-callback-ref';
import type { ToastPropsInterface } from '../../toast/types/toast.types';
import type {
  ToastObjectInterface,
  UseToastQueueArgumentsInterface,
  UseToastQueueReturnTypeInterface,
} from '../types/use-toast-queue.types';

export const useToastQueue = (
  opts: UseToastQueueArgumentsInterface = {},
): UseToastQueueReturnTypeInterface => {
  const { maxSize = 3 } = opts;

  const [activeIds, setActiveIds] = useState<Array<number>>([]);
  const [pendingIds, setPendingIds] = useState<Array<number>>([]);

  const toasts = useRef<Record<number, ToastObjectInterface>>({});
  const id = useRef(-1);

  const pause = useCallbackRef((toastId: number) => {
    const toast = toasts.current[toastId];

    if (!toast || !toast.timeout) {
      return;
    }

    clearTimeout(toast.timeout);
    delete toast.timeout;
    toast.elapsed += Date.now() - toast.start;
  });

  const resume = useCallbackRef((toastId: number) => {
    const toast = toasts.current[toastId];

    if (!toast || toast.timeout) {
      return;
    }

    // Note: A duration of `0` should not initiate a dismiss timeout
    if (toast.options?.duration) {
      const handleDismiss = () => {
        dismiss(toastId);
      };
      toast.timeout = setTimeout(handleDismiss, toast.options.duration - toast.elapsed);
    }
  });

  const pauseAll = useCallbackRef(() => {
    activeIds.forEach(pause);
  });

  const resumeAll = useCallbackRef(() => {
    activeIds.forEach(resume);
  });

  const dismiss = useCallbackRef((toastId: number) => {
    if (typeof toastId === 'undefined') return;

    if (activeIds.includes(toastId)) {
      setActiveIds(activeIds.filter((tid) => tid !== toastId));
    }

    if (pendingIds.includes(toastId)) {
      setPendingIds(pendingIds.filter((tid) => tid !== toastId));
    }
  });

  const dismissAll = useCallbackRef(() => {
    if (activeIds.length) {
      setActiveIds([]);
      setPendingIds([]);
      toasts.current = {};
    }
  });

  // Moves toast from pending to active and initiates its timeout
  useEffect(() => {
    if (pendingIds.length && activeIds.length < maxSize) {
      const [toastId, ...pendingTail] = pendingIds;
      setActiveIds([...activeIds, toastId]);
      setPendingIds(pendingTail);
      resume(toastId);
    }
  }, [pendingIds, activeIds, maxSize, resume]);

  // Merges duration options from Provider props and enqueue() options
  const getToastDuration = useCallbackRef(
    ({
      tone,
      providerProps,
      enqueueProps,
    }: {
      tone?: ToastPropsInterface['tone'];
      providerProps?: UseToastQueueArgumentsInterface;
      enqueueProps?: UseToastQueueArgumentsInterface;
    }): number => {
      const defaultDuration = 5000;
      const duration = enqueueProps?.duration ?? providerProps?.duration ?? defaultDuration;

      if (typeof duration === 'number') {
        return duration;
      }

      if (tone && tone in duration) {
        return duration[tone] ?? defaultDuration;
      }

      return defaultDuration;
    },
  );

  const enqueue = useCallbackRef(
    (node: ReactElement<ToastPropsInterface>, options?: UseToastQueueArgumentsInterface) => {
      id.current += 1;
      const toastId = id.current;

      const toast: ToastObjectInterface = {
        id: toastId,
        elapsed: 0,
        start: Date.now(),
        options: {
          ...opts,
          ...options,
          duration: getToastDuration({
            tone: node?.props?.tone ?? 'info', // Handles default Toast tone value
            providerProps: opts,
            enqueueProps: options,
          }),
        },
        node,
      };

      toasts.current[toastId] = toast;
      setPendingIds([...pendingIds, toastId]);
    },
  );

  const currentToasts = useMemo(() => {
    return activeIds.map((tid) => toasts.current[tid]);
  }, [activeIds]);

  return {
    enqueue,
    dismissAll,
    dismiss,
    toasts: currentToasts,
    activeIds,
    pendingIds,
    pause,
    pauseAll,
    resume,
    resumeAll,
  };
};
